import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight,
  Maximize2,
} from "lucide-react";
import { fetchCollection, fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import SectionFadeIn from "@/components/SectionFadeIn";
import { getRouteState } from "@/lib/navigation";
import PageMeta from "@/components/PageMeta";
import { CANVAS_COLLECTIONS } from "@/lib/canvasCollections";

const HERO_IMG =
  "https://images.unsplash.com/photo-1758448755778-90ebf4d0f1e7?w=1400&q=80";

function isCanvasProduct(product) {
  const productType = (product.productType || product.product_type || "").toLowerCase();
  return productType.includes("canvas") && !productType.includes("mural");
}

function sortProductsWithImagesFirst(list) {
  return [...list].sort((a, b) => {
    const aHas = a.images?.length > 0 ? 0 : 1;
    const bHas = b.images?.length > 0 ? 0 : 1;
    return aHas - bHas;
  });
}

export default function CanvasLanding() {
  const [products, setProducts] = useState([]);
  const location = useLocation();

  useEffect(() => {
    let active = true;

    async function loadCanvas() {
      try {
        const collectionData = await fetchCollection("canvas-prints");
        const collectionProducts = (collectionData.collection?.products || []).filter(isCanvasProduct);

        if (collectionProducts.length > 0) {
          if (active) {
            setProducts(sortProductsWithImagesFirst(collectionProducts));
          }
          return;
        }

        const fallbackData = await fetchProducts({ first: 250 });
        const fallbackProducts = (fallbackData.products || []).filter(isCanvasProduct);

        if (active) {
          setProducts(sortProductsWithImagesFirst(fallbackProducts));
        }
      } catch (err) {
        console.error("CanvasLanding: primary fetch failed, trying fallback", err);
        try {
          const fallbackData = await fetchProducts({ first: 250 });
          const fallbackProducts = (fallbackData.products || []).filter(isCanvasProduct);
          if (active) {
            setProducts(sortProductsWithImagesFirst(fallbackProducts));
          }
        } catch (innerErr) {
          console.error("CanvasLanding: fallback fetch also failed", innerErr);
          if (active) {
            setProducts([]);
          }
        }
      }
    }

    loadCanvas();

    return () => {
      active = false;
    };
  }, []);

  const preview = products.slice(0, 4);

  return (
    <>
      <PageMeta
        title="Canvas Prints"
        description="Gallery-quality canvas prints on premium stretched canvas. Browse hundreds of art-quality designs in landscape, portrait, and square. Archival inks, solid wood frames, ready to hang."
      />
      {/* ─── HERO ─── */}
      <section
        className="relative min-h-[68vh] md:min-h-[72vh] flex items-end pb-14 md:pb-18"
        data-testid="canvas-hero"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Luxury interior with canvas art"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0D14] via-[#0A0D14]/80 to-[#0A0D14]/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14] via-[#0A0D14]/30 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
          <p
            className="text-[11px] tracking-[0.35em] uppercase text-brand-gold font-body opacity-0 animate-fade-in-up"
          >
            Canvas Prints
          </p>
          <h1
            className="mt-4 font-heading text-[clamp(2.25rem,5.5vw,4.5rem)] tracking-tight leading-[0.92] text-brand-text max-w-[580px] opacity-0 animate-fade-in-up"
            style={{ animationDelay: "100ms" }}
            data-testid="canvas-heading"
          >
            Canvas That Commands Attention
          </h1>
          <p
            className="mt-4 text-brand-text-secondary text-[15px] max-w-md font-body leading-relaxed opacity-0 animate-fade-in-up"
            style={{ animationDelay: "200ms" }}
          >
            Premium stretched canvas, printed with archival inks and built to
            last. Choose a style, or bring your own image.
          </p>
          <div
            className="mt-8 flex flex-wrap gap-3 opacity-0 animate-fade-in-up"
            style={{ animationDelay: "300ms" }}
          >
            <Link
              to="/canvas-prints"
              state={getRouteState(location)}
              className="inline-flex items-center gap-2 bg-brand-gold text-brand-bg hover:bg-brand-gold-hover transition-colors duration-300 rounded-sm px-7 py-3 font-medium text-[13px] tracking-wide"
              data-testid="hero-browse-collection-cta"
            >
              Browse the Collection <ArrowRight size={15} />
            </Link>
            <Link
              to="/request-quote"
              state={getRouteState(location)}
              className="inline-flex items-center gap-2 border border-white/20 text-brand-text hover:border-brand-gold hover:text-brand-gold transition-colors duration-300 rounded-sm px-7 py-3 font-medium text-[13px] tracking-wide"
              data-testid="hero-custom-cta"
            >
              Custom Print Quote
            </Link>
          </div>
        </div>
      </section>

      {/* ─── BROWSE BY STYLE ─── */}
      <section
        className="pt-10 pb-16 md:pt-12 md:pb-20"
        data-testid="canvas-style-gallery"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionFadeIn>
            <div className="flex items-end justify-between gap-6 flex-wrap">
              <div>
                <p className="text-[11px] tracking-[0.25em] uppercase font-semibold text-brand-gold mb-3">
                  Browse by Style
                </p>
                <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-brand-text">
                  Find Your Canvas
                </h2>
                <p className="mt-2 text-[14px] text-brand-text-secondary/60 max-w-lg">
                  Choose a theme to explore designs, or bring your own image for a custom print.
                </p>
              </div>
              <Link
                to="/canvas-prints"
                state={getRouteState(location)}
                className="text-[12px] text-brand-text-secondary hover:text-brand-gold transition-colors duration-300 tracking-wide uppercase font-medium flex items-center gap-1.5 shrink-0"
                data-testid="canvas-style-view-all"
              >
                View all designs <ArrowRight size={13} />
              </Link>
            </div>
          </SectionFadeIn>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {CANVAS_COLLECTIONS.map((col, i) => {
              const Icon = col.icon;
              return (
                <SectionFadeIn key={col.slug} delay={i * 50}>
                  <Link
                    to={`/canvas-prints/${col.slug}`}
                    state={getRouteState(location)}
                    className="group relative block rounded-sm overflow-hidden border border-white/[0.06] hover:border-brand-gold/30 transition-all duration-300"
                    data-testid={`canvas-style-${col.slug}`}
                  >
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={col.image}
                        alt={col.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14]/90 via-[#0A0D14]/30 to-transparent" />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <div className="flex items-center gap-2 text-brand-gold mb-1.5">
                        <Icon size={15} />
                      </div>
                      <h3 className="text-[14px] font-semibold text-brand-text group-hover:text-brand-gold transition-colors duration-300 leading-tight">
                        {col.title}
                      </h3>
                      <p className="mt-1 text-[11px] text-brand-text-secondary/50 line-clamp-1">
                        {col.short}
                      </p>
                    </div>
                  </Link>
                </SectionFadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── FEATURED CANVAS (4 products, images-first) ─── */}
      {preview.length > 0 && (
        <section
          className="pb-18 md:pb-20 bg-brand-surface/30"
          data-testid="canvas-preview-section"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <SectionFadeIn>
              <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                  <p className="text-[11px] tracking-[0.25em] uppercase font-semibold text-brand-gold mb-3">
                    From the Collection
                  </p>
                  <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-brand-text">
                    A Few of Our Favourites
                  </h2>
                </div>
                <Link
                  to="/canvas-prints"
                  state={getRouteState(location)}
                  className="text-[12px] text-brand-text-secondary hover:text-brand-gold transition-colors duration-300 tracking-wide uppercase font-medium flex items-center gap-1.5 shrink-0"
                  data-testid="preview-browse-all"
                >
                  View all <ArrowRight size={13} />
                </Link>
              </div>
            </SectionFadeIn>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
              {preview.map((product, i) => (
                <SectionFadeIn key={product.id} delay={i * 80}>
                  <ProductCard
                    product={product}
                    basePath="/canvas-prints"
                    variant="canvas"
                  />
                </SectionFadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── COLLECTION CTA ─── */}
      <section
        className="border-y border-white/[0.05] bg-brand-surface/50"
        data-testid="collection-cta-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-16 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-2xl sm:text-[1.7rem] tracking-tight text-brand-text">
              Ready to browse?
            </h3>
            <p className="mt-1 text-[13px] text-brand-text-secondary">
              See every canvas print we currently have in stock, then open any piece to choose your preview, size, and frame.
            </p>
          </div>
          <Link
            to="/canvas-prints"
            state={getRouteState(location)}
            className="inline-flex items-center gap-2 bg-brand-gold text-brand-bg hover:bg-brand-gold-hover transition-colors duration-300 rounded-sm px-8 py-3.5 font-medium text-[13px] tracking-wide shrink-0"
            data-testid="view-full-collection-cta"
          >
            View Full Collection <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      {/* ─── CUSTOM PATH (secondary) ─── */}
      <section
        className="py-14 md:py-16 max-w-7xl mx-auto px-6 md:px-12"
        data-testid="custom-canvas-section"
      >
        <SectionFadeIn>
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-[11px] tracking-[0.25em] uppercase font-semibold text-brand-gold mb-3">
              Your Image, Our Canvas
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-brand-text leading-tight">
              Bring Your Own Artwork
            </h2>
            <p className="mt-3 text-[14px] text-brand-text-secondary leading-relaxed max-w-md mx-auto">
              Send us a high-resolution file and we&rsquo;ll print it on
              premium stretched canvas, sized exactly for your wall.
            </p>
            <Link
              to="/request-quote"
              state={getRouteState(location)}
              className="inline-flex items-center gap-2 mt-7 border border-brand-gold/60 text-brand-gold hover:bg-brand-gold/10 transition-colors duration-300 rounded-sm px-7 py-3 font-medium text-[13px] tracking-wide"
              data-testid="custom-upload-cta"
            >
              Request Custom Print <ArrowRight size={15} />
            </Link>
          </div>
        </SectionFadeIn>
      </section>
    </>
  );
}
